/**
 * Хелперы «Журнала»: период → from-граница, машинное action → человеческая
 * подпись с иконкой (по ключевым словам — терпимо к вариантам нейминга BE),
 * компактный превью payload и группировка ленты по дням.
 */

import dayjs from 'dayjs';
import {
  Archive,
  Building2,
  KeyRound,
  type LucideIcon,
  Pencil,
  Phone,
  ScrollText,
  Tags,
  UserMinus,
  UserPlus,
} from 'lucide-react';

import type {AuditEntry} from '@services/audit';

export type PeriodKey = 'all' | 'day' | '7d' | '30d';

export const PERIODS: {key: PeriodKey; label: string}[] = [
  {key: 'all', label: 'Всё время'},
  {key: 'day', label: 'Сегодня'},
  {key: '7d', label: '7 дней'},
  {key: '30d', label: '30 дней'},
];

export const periodFrom = (p: PeriodKey): string | undefined => {
  if (p === 'all') return undefined;
  if (p === 'day') return dayjs().startOf('day').toISOString();
  return dayjs()
    .subtract(p === '7d' ? 7 : 30, 'day')
    .toISOString();
};

export type ActionMeta = {label: string; icon: LucideIcon; danger?: boolean};

/** Правила по ключевым словам: первое совпадение выигрывает. */
const RULES: {test: RegExp; meta: ActionMeta}[] = [
  {test: /archive/, meta: {label: 'Архивация клиента', icon: Archive, danger: true}},
  {test: /reset.*password|password.*reset/, meta: {label: 'Сброс пароля админа', icon: KeyRound}},
  {
    test: /admin.?phone|phone.*change|change.*phone/,
    meta: {label: 'Смена номера админа', icon: Phone},
  },
  {
    test: /(add|create).*doctor|doctor.*(add|create)/,
    meta: {label: 'Добавлен врач', icon: UserPlus},
  },
  {
    test: /(remove|delete).*doctor|doctor.*(remove|delete)/,
    meta: {label: 'Удалён врач', icon: UserMinus},
  },
  {test: /doctor/, meta: {label: 'Правка врача', icon: Pencil}},
  {test: /tariff.*solo|solo.*tariff/, meta: {label: 'Новая версия соло-тарифа', icon: Tags}},
  {test: /tariff.*clinic|clinic.*tariff/, meta: {label: 'Новая версия тарифа клиник', icon: Tags}},
  {test: /tariff/, meta: {label: 'Изменение тарифа', icon: Tags}},
  {
    test: /create.*(client|account)|(client|account).*create/,
    meta: {label: 'Создан аккаунт', icon: Building2},
  },
  {
    test: /update.*client|client.*update|patch/,
    meta: {label: 'Правка карточки клиента', icon: Pencil},
  },
  {test: /application/, meta: {label: 'Заявка клиники', icon: ScrollText}},
];

export const actionMeta = (action: string): ActionMeta => {
  const key = action.toLowerCase();
  for (const rule of RULES) if (rule.test.test(key)) return rule.meta;
  return {label: action, icon: ScrollText};
};

/** Первые пары payload в строку: «reason: …, version: 8». */
export const payloadPreview = (payload: AuditEntry['payload']): string | null => {
  if (payload === null) return null;
  const pairs = Object.entries(payload)
    .filter(([, v]) => v !== null && typeof v !== 'object')
    .slice(0, 2)
    .map(([k, v]) => `${k}: ${String(v)}`);
  return pairs.length === 0 ? null : pairs.join(' · ');
};

export const fmtAuditTime = (iso: string): string => dayjs(iso).format('HH:mm');

const dayLabel = (iso: string): string => {
  const d = dayjs(iso);
  if (d.isSame(dayjs(), 'day')) return 'Сегодня';
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Вчера';
  return d.format('D MMM YYYY').replace('.', '');
};

export type DayGroup = {day: string; items: AuditEntry[]};

/** Лента (свежие сверху) → группы по дням, порядок сохраняется. */
export const groupByDay = (items: AuditEntry[]): DayGroup[] => {
  const groups: DayGroup[] = [];
  for (const item of items) {
    const day = dayLabel(item.createdAt);
    const last = groups[groups.length - 1];
    if (last !== undefined && last.day === day) last.items.push(item);
    else groups.push({day, items: [item]});
  }
  return groups;
};
